import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { MarkdownEditor } from 'components/blocks/markdown-editor';
import { VisibilityField } from 'components/blocks/visibility-field';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from 'components/ui/form';
import { useAddText, useGetFolderList } from 'features/chat/hooks/use-kb';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import {
  DEFAULT_RESOURCE_VISIBILITY,
} from 'types/visibility';

const MIN_LENGTH = 100;
const MAX_LENGTH = 10000;

const createTextKbFormSchema = (t: (key: string, options?: Record<string, unknown>) => string) =>
  z.object({
    text: z
      .string()
      .trim()
      .nonempty({ message: t('INFORMATION_IS_REQUIRED') })
      .min(MIN_LENGTH, t('AT_LEAST_MIN_CHARACTERS_REQUIRED', { min: MIN_LENGTH }))
      .max(MAX_LENGTH, t('MAX_CHARACTERS_ALLOWED', { max: MAX_LENGTH }))
      .trim(),
    visibility: z.enum(['private', 'public']),
  });

type TextKbFormValues = z.infer<ReturnType<typeof createTextKbFormSchema>>;

const textKbFormDefaultValues: TextKbFormValues = {
  text: '',
  visibility: DEFAULT_RESOURCE_VISIBILITY,
};

type AddKbWriteupModalProps = {
  folderId: string;
  projectKey: string;
};

export function AddKbWriteupModal({ folderId, projectKey }: AddKbWriteupModalProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const textKbFormSchema = useMemo(() => createTextKbFormSchema(t), [t]);
  const form = useForm<TextKbFormValues>({
    defaultValues: textKbFormDefaultValues,
    resolver: zodResolver(textKbFormSchema),
    mode: 'onChange',
  });

  const validateContent = useMemo(
    () => (value: string): string | undefined => {
      const result = textKbFormSchema.safeParse({ text: value });
      if (!result.success) {
        return result.error.errors[0].message;
      }
      return undefined;
    },
    [textKbFormSchema]
  );

  const { isValid } = form.formState;
  const textValue = form.watch('text') || '';
  const normalizedLength = textValue.replace(/\s+/g, ' ').trim().length;
  const isUnderMinLength = normalizedLength < MIN_LENGTH;
  const { mutateAsync: addTextMutate, isPending } = useAddText();
  const { data: folderListData } = useGetFolderList(projectKey, !!projectKey);
  const folder = useMemo(
    () => folderListData?.data?.folders?.find((f) => f._id === folderId),
    [folderId, folderListData?.data?.folders]
  );
  const canEdit = useIsOwner(folder);

  const resetState = () => {
    form.reset(textKbFormDefaultValues);
  };

  const submitHandler = async (values: TextKbFormValues) => {
    if (!canEdit) return;

    try {
      const response = await addTextMutate({
        text: values.text,
        project_key: projectKey,
        folder_id: folderId,
        visibility: values.visibility,
      });

      if (!response.is_success) {
        throw new Error(response.detail || t('FAILED_TO_ADD_WRITEUP_TO_KB'));
      }

      toast({
        variant: 'success',
        title: t('WRITEUP_ADDED'),
        description: t('KNOWLEDGE_ENTRY_ADDED_TO_FOLDER'),
      });
      resetState();
      setOpen(false);
    } catch (error) {
      toast({
        title: t('UNABLE_TO_ADD_WRITEUP'),
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen && !canEdit) return;
        setOpen(nextOpen);
        if (!nextOpen) {
          resetState();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!canEdit}>
          <Plus className="h-4 w-4" />
          {t('ADD_WRITEUP')}
        </Button>
      </DialogTrigger>

      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-lg flex-col overflow-hidden p-5 md:min-w-[720px] md:max-w-[720px]">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('ADD_KNOWLEDGE')}</DialogTitle>
          <DialogDescription>{t('ENTER_ACCURATE_KB_INFORMATION')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(submitHandler)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
              {/* <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem className="mb-4">
                    <FormControl>
                      <VisibilityField
                        value={field.value}
                        onValueChange={field.onChange}
                        idPrefix="add-kb-writeup-visibility"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}

              <FormField
                control={form.control}
                name="text"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <MarkdownEditor
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t('KB_CONTENT_PLACEHOLDER')}
                        disabled={isPending}
                        minLength={MIN_LENGTH}
                        maxLength={MAX_LENGTH}
                        showCharacterCount
                        contentClassName="min-h-32 max-h-none"
                        error={fieldState.error?.message}
                        validate={validateContent}
                        showToolbar
                        showModeToggle
                        defaultMode="edit"
                        isInvalid={!!fieldState.error}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-4 shrink-0 flex flex-row justify-start gap-2 border-t border-border pt-4">
              <Button
                type="submit"
                size="sm"
                className="min-w-20 flex-1 sm:flex-none"
                disabled={isPending || !isValid || isUnderMinLength}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('ADDING')}
                  </>
                ) : (
                  t('ADD')
                )}
              </Button>
              <DialogClose asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none sm:w-20">
                  {t('CANCEL')}
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
